import React, { useState, useRef } from 'react';
import { 
 Upload, 
 Check, 
 Trash2, 
 Star, 
 Loader2,
 Image as ImageIcon,
 Grid3x3,
 List
} from 'lucide-react';
import { 
 useUserImages, 
 useUploadImage, 
 useDeleteImage, 
 useSetDefaultImage 
} from '../../services/userImageService';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirm } from '../../contexts/ConfirmContext';
import { cn } from '../../utils/cn';

const ImageBrowser = ({ 
 imageType = null,
 /** When set, overrides listing query (e.g. { non_portfolio: true }). Upload type uses uploadImageType / imageType. */
 listQueryParams = null,
 /** Stored image_type on upload; defaults to imageType or 'general'. */
 uploadImageType = null,
 onSelectImage = null,
 showUpload = true,
 showDelete = true,
 showSetDefault = true,
 maxImages = null,
 className = ''
}) => {
 const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
 const [selectedImage, setSelectedImage] = useState(null);
 const fileInputRef = useRef(null);
 const [uploadProgress, setUploadProgress] = useState(null);
 const queryClient = useQueryClient();
 const { confirm } = useConfirm();

 const queryParams = listQueryParams ?? (imageType ? { image_type: imageType } : {});
 const { data: imagesResponse, isLoading } = useUserImages(queryParams);
 const images = imagesResponse?.data || [];

 const uploadMutation = useUploadImage({
 onSuccess: (data) => {
 // Set to 100% briefly to show completion
 setUploadProgress(100);
 
 setTimeout(() => {
 setUploadProgress(null);
 }, 800); // Clear after showing completion
 
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 
 // Force refetch images to show the new one immediately - invalidate all user-images queries
 queryClient.invalidateQueries({ queryKey: ['user-images'] });
 },
 onError: (error) => {
 setUploadProgress(null);
 const message = error?.response?.data?.message || error?.message || 'Image upload failed';
 alert(message);
 console.error('Upload error:', error);
 }
 });

 const deleteMutation = useDeleteImage();
 const setDefaultMutation = useSetDefaultImage();

 const handleFileSelect = (e) => {
 const file = e.target.files?.[0];
 if (!file) return;

 // Validate file
 const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
 const maxSize = 10 * 1024 * 1024; // 10MB

 if (!validTypes.includes(file.type)) {
 alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
 return;
 }

 if (file.size > maxSize) {
 alert('File size must be less than 10MB');
 return;
 }

 // Check max images limit (0 means none allowed; null/undefined means unlimited)
 if (maxImages != null && images.length >= maxImages) {
 alert(`Maximum ${maxImages} images allowed`);
 return;
 }

 // Create form data
 const formData = new FormData();
 formData.append('image', file);
 formData.append('image_type', uploadImageType ?? imageType ?? 'general');

 setUploadProgress(0);
 
 // Create progress callback that updates state
 const handleProgress = (percent) => {
 setUploadProgress(percent);
 };
 
 // Start upload with progress tracking
 uploadMutation.mutate(
 { formData, onUploadProgress: handleProgress },
 {
 onSuccess: () => {
 setUploadProgress(null);
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 },
 onError: () => {
 setUploadProgress(null);
 }
 }
 );
 };

 const handleDelete = async (imageId, e) => {
 e.stopPropagation();
 const ok = await confirm({
 title: 'Delete image?',
 message: 'This image will be removed from your library. This cannot be undone.',
 confirmLabel: 'Delete',
 variant: 'danger',
 });
 if (ok) deleteMutation.mutate(imageId);
 };

 const handleSetDefault = (imageId, e) => {
 e.stopPropagation();
 setDefaultMutation.mutate(imageId);
 };

 const handleImageClick = (image) => {
 if (onSelectImage) {
 onSelectImage(image);
 }
 setSelectedImage(image.id === selectedImage?.id ? null : image.id);
 };

 if (isLoading) {
 return (
 <div className="flex items-center justify-center py-12">
 <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
 </div>
 );
 }

 return (
 <div className={cn("space-y-4", className)}>
 {/* Header with Upload and View Toggle */}
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <ImageIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
 <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
 My Images ({images.length})
 </h3>
 {maxImages !== null && (
 <span className="text-xs text-gray-500 dark:text-gray-400">
 {images.length}/{maxImages} used
 </span>
 )}
 </div>

 <div className="flex items-center gap-2">
 {/* View Mode Toggle */}
 <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700">
 <button
 onClick={() => setViewMode('grid')}
 className={cn(
 "p-2 rounded-lg transition-all",
 viewMode === 'grid'
 ? "bg-surface dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm"
 : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
 )}
 aria-label="Grid view"
 >
 <Grid3x3 className="w-4 h-4" />
 </button>
 <button
 onClick={() => setViewMode('list')}
 className={cn(
 "p-2 rounded-lg transition-all",
 viewMode === 'list'
 ? "bg-surface dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm"
 : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
 )}
 aria-label="List view"
 >
 <List className="w-4 h-4" />
 </button>
 </div>

 {/* Upload Button */}
 {showUpload && (
 <label className="inline-flex items-center px-4 py-2 bg-ink text-white !rounded-xl hover:bg-ink cursor-pointer transition-colors">
 <Upload className="w-4 h-4 mr-2" />
 Upload Image
 <input
 ref={fileInputRef}
 type="file"
 accept="image/*"
 onChange={handleFileSelect}
 className="hidden"
 disabled={uploadMutation.isPending}
 />
 </label>
 )}
 </div>
 </div>

 {/* Upload Progress */}
 {uploadProgress !== null && uploadProgress >= 0 && uploadProgress < 100 && (
 <div className="bg-surface-muted dark:bg-surface-muted border border-border dark:border-border rounded-xl p-3">
 <div className="flex items-center justify-between mb-2">
 <div className="flex items-center gap-2">
 <Loader2 className="w-4 h-4 animate-spin text-ink dark:text-ink-secondary" />
 <span className="text-sm font-medium text-ink dark:text-ink">Uploading...</span>
 </div>
 <span className="text-sm text-ink dark:text-ink-tertiary">
 {uploadProgress}%
 </span>
 </div>
 <div className="w-full bg-surface-muted rounded-full h-2">
 <div
 className="bg-ink h-2 rounded-full transition-all duration-300"
 style={{ width: `${uploadProgress}%` }}
 />
 </div>
 </div>
 )}

 {/* Images Grid/List */}
 {images.length === 0 ? (
 <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-surface dark:bg-gray-900 p-12 text-center">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-surface-muted dark:bg-gray-800 text-gray-400">
 <ImageIcon className="w-7 h-7" />
 </div>
 <p className="text-gray-600 dark:text-gray-400 mb-3">No images uploaded yet</p>
 {showUpload && (
 <label className="inline-flex items-center px-4 py-2 bg-ink text-white !rounded-xl hover:bg-ink cursor-pointer transition-colors">
 <Upload className="w-4 h-4 mr-2" />
 Upload Your First Image
 <input
 ref={fileInputRef}
 type="file"
 accept="image/*"
 onChange={handleFileSelect}
 className="hidden"
 />
 </label>
 )}
 </div>
 ) : (
 <div
 className={cn(
 viewMode === 'grid'
 ? "flex flex-wrap gap-1"
 : "space-y-2"
 )}
 >
 {images.map((image) => (
 <div
 key={image.id}
 onClick={() => handleImageClick(image)}
 className={cn(
 "relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all",
 selectedImage === image.id
 ? "border-ink ring-2 ring-border "
 : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
 viewMode === 'grid' ? "w-32 h-32 flex-shrink-0" : "flex items-center gap-3 p-2 bg-surface dark:bg-gray-900"
 )}
 >
 {/* Image */}
 <div className={cn(
 "relative bg-gray-100 dark:bg-gray-800 overflow-hidden",
 viewMode === 'grid' ? "w-full h-full" : "w-16 h-16 flex-shrink-0 rounded-lg"
 )}>
 <img
 src={image.image_url}
 alt={image.description || image.file_name}
 className="w-full h-full object-cover"
 />

 {/* Overlay with actions */}
 <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
 {showSetDefault && (
 <button
 onClick={(e) => handleSetDefault(image.id, e)}
 className={cn(
 "p-2 rounded-xl transition-colors",
 image.is_default
 ? "bg-yellow-500 text-white"
 : "bg-surface/90 text-gray-700 hover:bg-surface"
 )}
 aria-label="Set as default"
 title={image.is_default ? "Default image" : "Set as default"}
 >
 <Star className={cn(
 "w-4 h-4",
 image.is_default && "fill-current"
 )} />
 </button>
 )}

 {showDelete && (
 <button
 onClick={(e) => handleDelete(image.id, e)}
 className="p-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors"
 aria-label="Delete image"
 title="Delete image"
 >
 <Trash2 className="w-4 h-4" />
 </button>
 )}

 {onSelectImage && (
 <button
 onClick={(e) => {
 e.stopPropagation();
 onSelectImage(image);
 }}
 className="p-2 rounded-xl bg-ink text-white hover:bg-ink transition-colors"
 aria-label="Select image"
 title="Select image"
 >
 <Check className="w-4 h-4" />
 </button>
 )}
 </div>

 {/* Default Badge */}
 {image.is_default && (
 <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
 <Star className="w-3 h-3 fill-current" />
 Default
 </div>
 )}

 {/* Selected Indicator */}
 {selectedImage === image.id && (
 <div className="absolute top-2 right-2 bg-ink text-white p-1 rounded-xl">
 <Check className="w-4 h-4" />
 </div>
 )}
 </div>

 {/* List View Details */}
 {viewMode === 'list' && (
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
 {image.file_name}
 </p>
 {image.description && (
 <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
 {image.description}
 </p>
 )}
 <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
 <span>{(image.file_size / 1024).toFixed(1)} KB</span>
 <span>{new Date(image.created_at).toLocaleDateString()}</span>
 </div>
 </div>
 )}
 </div>
 ))}
 </div>
 )}
 </div>
 );
};

export default ImageBrowser;

